import { useState, useRef } from "react";
import { Button } from "src/components/ui/button";
import { Plus } from "lucide-react";
import IOTable, { IOTableRef } from "./io-table";
import InvoiceTable from "./invoice-table";
import { useGetInvoiceGroupsQuery, useGetInvoiceGroupDetailsQuery, useGetCompaniesQuery, useCreateInvoiceGroupMutation } from "../endpoints/billing.endpoints";
import { BillingTableProps, InvoiceGroup } from "../types/billing.types";
import SearchInput from "../../../components/search-input/search-input";
import NewInvoiceGroupModal from "./new-invoice-group-modal";
import CustomMappingRulesModal from "./custom-mapping-rules-modal";

const BillingTable: React.FC<BillingTableProps> = ({ activeFilter }) => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [isNewInvoiceGroupModalOpen, setIsNewInvoiceGroupModalOpen] = useState(false);
  const [isCustomMappingRulesModalOpen, setIsCustomMappingRulesModalOpen] = useState(false);
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const [previewGroupId, setPreviewGroupId] = useState<string | undefined>(undefined);
  const ioTableRef = useRef<IOTableRef>(null);

  const { data: invoiceGroupsData } = useGetInvoiceGroupsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });


  const { data: invoiceGroupDetails, isLoading: invoiceGroupDetailsLoading } = useGetInvoiceGroupDetailsQuery(
    selectedGroupId,
    {
      skip: !selectedGroupId,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: companies } = useGetCompaniesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [createInvoiceGroup] = useCreateInvoiceGroupMutation();

  // Select most recent invoice group by default
  const sortedInvoiceGroups = (invoiceGroupsData || [])
    .slice()
    .sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return b.month - a.month;
    });

  // Auto-select the most recent invoice group by default, or the newly created one
  if (sortedInvoiceGroups.length > 0 && !selectedGroupId) {
    setSelectedGroupId(sortedInvoiceGroups[0].id);
  }
  const handleInvoiceGroupCreated = (newGroupId: string, wasGenerated?: boolean) => {
    setSelectedGroupId(newGroupId);
    setIsGeneratingInvoices(wasGenerated || false);
  };

  const formatMonthYear = (month: number, year: number): string => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  const handleCreateNewInsertionOrder = () => {
    ioTableRef.current?.createNew();
  };

  const handleOpenNewInvoiceGroupModal = () => {
    setPreviewGroupId(undefined); // Clear preview mode
    setIsNewInvoiceGroupModalOpen(true);
  };

  const handleOpenCustomMappingRules = () => {
    setIsCustomMappingRulesModalOpen(true);
  };

  const handleOpenPreviewModal = (groupId: string) => {
    setSelectedGroupId(groupId);
    setPreviewGroupId(groupId); // Set preview mode
    setIsNewInvoiceGroupModalOpen(true);
  };

  const handleGenerateInvoices = async(groupId: string) => {
    const invoiceGroup = sortedInvoiceGroups.find(group => group.id === groupId);
    if (!invoiceGroup) return;

    setIsGeneratingInvoices(true);

    try {
      // Trigger actual invoice generation with isInitial=false
      await createInvoiceGroup({
        month: invoiceGroup.month,
        year: invoiceGroup.year,
        isInitial: false,
      }).unwrap();
    } catch (error) {
      console.error('Failed to generate invoices:', error);
      setIsGeneratingInvoices(false);
    }
  };

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4 mr-auto">
          {/* Filter Type Display */}
          <div className="text-lg font-medium text-gray-700">
            {activeFilter === 'insertion-orders' ? 'Insertion Orders' : 'Invoices'}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4 ml-auto">
          <div className="h-6 border-l border-gray-300" />

          {activeFilter === 'invoices' && sortedInvoiceGroups.length > 0 && (
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm min-w-[160px]"
            >
              {sortedInvoiceGroups.map((group) => (
                <option
                  key={group.id}
                  value={group.id}
                >
                  {formatMonthYear(group.month, group.year)}
                </option>
              ))}
            </select>
          )}

          {activeFilter === 'insertion-orders' && (
            <Button
              size="med"
              variant="outline"
              onClick={handleCreateNewInsertionOrder}
              className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
            >
              <Plus className="h-4 w-4" />
              New IO
            </Button>
          )}

          {activeFilter === 'invoices' && (
            <Button
              size="med"
              variant="outline"
              onClick={handleOpenNewInvoiceGroupModal}
              className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
            >
              <Plus className="h-4 w-4" />
              New Month
            </Button>
          )}
        </div>
      </div>

      {/* Search Row */}
      <div className="flex items-center justify-between">
        <SearchInput
          placeholder={`Search ${activeFilter === 'insertion-orders' ? 'insertion orders' : 'invoices'}...`}
          value={searchValue}
          onChange={setSearchValue}
          debounceMs={500}
          className="w-96"
        />

        <div className="flex items-center gap-4">
          {/* Additional controls could go here */}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden relative">
        {activeFilter === 'insertion-orders' ? (
          <IOTable
            ref={ioTableRef}
            searchValue={searchValue}
          />
        ) : (
          <InvoiceTable
            searchValue={searchValue}
            viewHidden={false}
            selectedGroupId={selectedGroupId}
            invoiceGroupDetails={invoiceGroupDetails}
            isLoadingDetails={invoiceGroupDetailsLoading}
            companies={companies}
            isGeneratingInvoices={isGeneratingInvoices}
            onGenerationComplete={() => setIsGeneratingInvoices(false)}
            onOpenPreviewModal={handleOpenPreviewModal}
            onGenerateInvoices={handleGenerateInvoices}
          />
        )}
      </div>

      {/* Modals */}
      <NewInvoiceGroupModal
        isOpen={isNewInvoiceGroupModalOpen}
        onClose={() => {
          setIsNewInvoiceGroupModalOpen(false);
          setPreviewGroupId(undefined);
        }}
        existingInvoiceGroups={(invoiceGroupsData as unknown as InvoiceGroup[]) || []}
        onCreated={handleInvoiceGroupCreated}
        previewGroupId={previewGroupId}
      />
      <CustomMappingRulesModal
        isOpen={isCustomMappingRulesModalOpen}
        onClose={() => setIsCustomMappingRulesModalOpen(false)}
      />
    </div>
  );
};

export default BillingTable;